import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  IconButton,
  Button,
  Autocomplete,
  MenuItem,
  useMediaQuery,
  Tooltip,
  Chip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import CEDatePicker from "components/CEDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import dayjs from "dayjs";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { toExpenseDateOnly } from "./expenseDateUtils";
import Overlay from "components/Overlay/Overlay";

const ExpenseNewEdit = () => {
  usePageTitle("New Expense");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar } = useStateContext();

  const isEditing = !!overlayData?.expense;
  const wasFinalizedOnOpen =
    isEditing && (overlayData?.expense?.Status || "Draft") === "Finalized";

  const closeOverlay = () => setOverlayName("");
  
  // Determine default date based on Expenses filter year
  const getDefaultExpenseDate = useCallback(() => {
    const currentYear = dayjs().year();
    const targetYear = Number(overlayData?.selectedYear) || currentYear;
    const targetMonth = targetYear === currentYear ? dayjs().month() : 0; // current month if current year, else January
    return dayjs()
      .year(targetYear)
      .month(targetMonth)
      .date(1)
      .format("YYYY-MM-DD");
  }, [overlayData?.selectedYear]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get("/expenses/categories");
      setCategories(response.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      showSnackbar("Error loading categories", theme, "error");
    }
  }, [showSnackbar, theme]);

  const fetchDetails = useCallback(async (subCategoryId) => {
    try {
      const response = await api.get(
        `/expenses/details?subCategoryId=${subCategoryId}`,
      );
      setDetails(response.data || []);
    } catch (error) {
      console.error("Error fetching details:", error);
      showSnackbar("Error loading expense details", theme, "error");
      setDetails([]);
    }
  }, [showSnackbar, theme]);

  const fetchSubcategories = useCallback(async (categoryId, presetSubcategoryId = null) => {
    try {
      const response = await api.get(
        `/expenses/subcategories?categoryId=${categoryId}`,
      );
      const data = response.data || [];
      setSubcategories(data);

      // If editing and we have a subcategory to preset
      if (presetSubcategoryId) {
        const found = data.find((s) => s.id === presetSubcategoryId);
        if (found) {
          setSelectedSubcategory(found);
          fetchDetails(found.id);
        }
      } else {
        setSelectedSubcategory(null);
        setDetails([]);
        setSelectedDetailId(null);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      showSnackbar("Error loading subcategories", theme, "error");
    }
  }, [fetchDetails, showSnackbar, theme]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [details, setDetails] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedDetailId, setSelectedDetailId] = useState(null);

  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(getDefaultExpenseDate);
  const [description, setDescription] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Other");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [deductiblePercentage, setDeductiblePercentage] = useState(0);
  const [status, setStatus] = useState("Draft");
  const [editReason, setEditReason] = useState("");

  const [repetition, setRepetition] = useState("None");
  const [errors, setErrors] = useState({});

  // Current month for conditional repetition options
  const currentMonth = dayjs().month(); // 0-11, where 0 is January

  // Update default date if the overlay provides a different year (only for new)
  useEffect(() => {
    if (!isEditing) {
      setExpenseDate(getDefaultExpenseDate());
    }
  }, [getDefaultExpenseDate, isEditing]);

  // Fetch categories on mount (or use Quick Create prefetch when provided)
  useEffect(() => {
    if (overlayData?.Categories?.length) {
      setCategories(overlayData.Categories);
    } else {
      fetchCategories();
    }
  }, [fetchCategories, overlayData?.Categories]);

  // Prefill form when editing
  useEffect(() => {
    if (!isEditing || !overlayData?.expense) return;

    const exp = overlayData.expense;
    setAmount(exp.Amount != null ? String(exp.Amount) : "");
    setExpenseDate(
      toExpenseDateOnly(exp.ExpenseDate) || getDefaultExpenseDate(),
    );
    setDescription(exp.Description || "");
    setPaymentMethod(exp.PaymentMethod || "Other");
    setReferenceNumber(exp.ReferenceNumber || "");
    setDeductiblePercentage(
      exp.DeductiblePercentage != null ? exp.DeductiblePercentage : 0,
    );
    setStatus(exp.Status || "Draft");
    setEditReason("");
    setRepetition("None");
    setSelectedDetailId(exp.Detail?.id || exp.DetailID || null);

    // category/subcategory preset after categories fetched
    if (categories && categories.length > 0) {
      const foundCat = categories.find(
        (c) => c.id === exp.Category?.id || c.id === exp.CategoryID,
      );
      if (foundCat) {
        setSelectedCategory(foundCat);
        fetchSubcategories(
          foundCat.id,
          exp.SubCategory?.id || exp.SubCategoryID,
        );
      }
    }
  }, [
    categories,
    fetchSubcategories,
    getDefaultExpenseDate,
    isEditing,
    overlayData,
  ]);

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    if (newValue) {
      fetchSubcategories(newValue.id);
    } else {
      setSubcategories([]);
      setDetails([]);
      setSelectedSubcategory(null);
      setSelectedDetailId(null);
    }
  };

  const validateForm = ({ requireEditReason = true } = {}) => {
    const newErrors = {};

    if (!selectedCategory) {
      newErrors.category = "Category is required";
    }

    if (
      amount === "" ||
      Number.isNaN(parseFloat(amount)) ||
      parseFloat(amount) < 0
    ) {
      newErrors.amount = "Amount must be 0 or greater";
    }
    if (!expenseDate) {
      newErrors.expenseDate = "Date is required";
    }
    if (deductiblePercentage < 0 || deductiblePercentage > 100) {
      newErrors.deductiblePercentage =
        "Deductible percentage must be between 0 and 100";
    }
    if (requireEditReason && wasFinalizedOnOpen && !String(editReason).trim()) {
      newErrors.editReason =
        "Edit reason is required when editing a finalized expense";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const expenseData = {
        CategoryID: selectedCategory.id,
        SubCategoryID: selectedSubcategory?.id || null,
        Amount: parseFloat(amount),
        ExpenseDate: expenseDate,
        Description: description || null,
        PaymentMethod: paymentMethod,
        ReferenceNumber: referenceNumber || null,
        DeductiblePercentage: parseFloat(deductiblePercentage) || 0,
        Status: status,
        ...(wasFinalizedOnOpen ? { EditReason: editReason.trim() } : {}),
      };

      if (isEditing && overlayData?.expense?.id) {
        await api.put(`/expenses/${overlayData.expense.id}`, expenseData);
        showSnackbar("Expense updated successfully", theme, "success");
      } else {
        // Create initial expense
        await api.post("/expenses", expenseData);

        // Handle repetition for new entries only
        if (repetition !== "None") {
          const selectedDate = dayjs(expenseDate);
          const month = selectedDate.month(); // 0-11
          const year = selectedDate.year();
          const day = selectedDate.date();

          let startMonth = repetition === "For the whole year" ? 0 : month;
          const endMonth = 11; // December

          for (let m = startMonth; m <= endMonth; m++) {
            if (m === month) continue; // Skip the original month

            const repeatedDate = dayjs()
              .year(year)
              .month(m)
              .date(day)
              .format("YYYY-MM-DD");

            const repeatedExpenseData = {
              ...expenseData,
              ExpenseDate: repeatedDate,
            };

            await api.post("/expenses", repeatedExpenseData);
          }
        }

        showSnackbar("Expense created successfully", theme, "success");
      }

      if (overlayData.onSaved) {
        overlayData.onSaved();
      }
      closeOverlay();
    } catch (error) {
      console.error("Error saving expense:", error);
      showSnackbar("Error saving expense", theme, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 1,
        flexShrink: 0,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", gap: 1 }}>
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={closeOverlay}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button variant="outlined" size="large" onClick={closeOverlay}>
            Discard
          </Button>
        )}

        {isMobile ? (
          <Tooltip
            title={
              isSubmitting
                ? isEditing
                  ? "Updating..."
                  : "Saving..."
                : isEditing
                  ? "Update"
                  : "Save"
            }
            placement="top"
          >
            <span>
              <IconButton
                disabled={isSubmitting}
                color="primary"
                onClick={handleSubmit}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <SaveRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            size="large"
            startIcon={<SaveRoundedIcon />}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isEditing ? "Update" : "Save"}
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Overlay
      id="expense-new-edit"
      open={true}
      onClose={closeOverlay}
      title={isEditing ? "Edit Expense" : "New Expense"}
      height="fit-content"
      footer={footer}
    >
      {isEditing && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Chip
            label={status || "Draft"}
            size="small"
            sx={{
              fontWeight: 700,
              color:
                (status || "Draft") === "Finalized"
                  ? theme.palette.success.dark
                  : theme.palette.warning.dark,
              backgroundColor:
                (status || "Draft") === "Finalized"
                  ? theme.palette.success.light
                  : theme.palette.warning.light,
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          p: "1rem 2.3rem 1rem 2.5rem",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
        }}
      >
          <Grid container spacing={2}>
            {/* Category */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth
                options={categories}
                getOptionLabel={(option) => option.Name || ""}
                value={selectedCategory}
                onChange={handleCategoryChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    size="small"
                    error={!!errors.category}
                    helperText={errors.category}
                  />
                )}
              />
            </Grid>

            {/* Subcategory */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth
                options={subcategories}
                getOptionLabel={(option) => option.Name || ""}
                value={selectedSubcategory}
                onChange={(event, newValue) => {
                  setSelectedSubcategory(newValue);
                  if (newValue) {
                    fetchDetails(newValue.id);
                  } else {
                    setDetails([]);
                    setSelectedDetailId(null);
                  }
                }}
                disabled={!selectedCategory}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Subcategory"
                    size="small"
                  />
                )}
              />
            </Grid>

            {/* Detail chips */}
            {selectedSubcategory && details.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: theme.palette.text.secondary }}
                >
                  Details
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {details.map((detail) => (
                    <Chip
                      key={detail.id}
                      label={detail.Name}
                      clickable
                      color={
                        selectedDetailId === detail.id ? "primary" : "default"
                      }
                      onClick={() => {
                        setSelectedDetailId(detail.id);
                        setDescription(detail.Name || "");
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Amount */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                size="small"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={!!errors.amount}
                helperText={errors.amount}
                inputProps={{ step: "0.01" }}
              />
            </Grid>

            {/* Expense Date */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CEDatePicker
                  label="Expense Date"
                  value={expenseDate ? dayjs(expenseDate) : null}
                  onChange={(newValue) => {
                    setExpenseDate(
                      newValue ? newValue.format("YYYY-MM-DD") : "",
                    );
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!errors.expenseDate,
                      helperText: errors.expenseDate,
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Payment Method */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Payment Method"
                select
                size="small"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Card">Card</MenuItem>
                <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                <MenuItem value="Check">Check</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>

            {/* Reference Number */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="Reference Number"
                size="small"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
              />
            </Grid>

            {/* Deductible Percentage */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="Deductible Percentage"
                type="number"
                size="small"
                value={deductiblePercentage}
                onChange={(e) => setDeductiblePercentage(e.target.value)}
                error={!!errors.deductiblePercentage}
                helperText={errors.deductiblePercentage}
                inputProps={{ min: 0, max: 100, step: "0.01" }}
              />
            </Grid>

            {!wasFinalizedOnOpen && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Status"
                  select
                  size="small"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="Draft">Draft</MenuItem>
                  <MenuItem value="Finalized">Finalized</MenuItem>
                </TextField>
              </Grid>
            )}

            {/* Repetition */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Repetition"
                select
                size="small"
                value={repetition}
                onChange={(e) => setRepetition(e.target.value)}
                disabled={isEditing}
              >
                <MenuItem value="None">None</MenuItem>
                {currentMonth !== 0 && (
                  <MenuItem value="For the rest of the year">
                    For the rest of the year (Current Month - Dec)
                  </MenuItem>
                )}
                <MenuItem value="For the whole year">
                  For the whole year (Jan - Dec)
                </MenuItem>
              </TextField>
            </Grid>

            {/* Description */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                size="small"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            {wasFinalizedOnOpen && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Edit Reason"
                  multiline
                  rows={2}
                  size="small"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  error={!!errors.editReason}
                  helperText={
                    errors.editReason ||
                    "Required when editing an expense that was already finalized."
                  }
                />
              </Grid>
            )}
          </Grid>
        </Box>
    </Overlay>
  );
};

export default ExpenseNewEdit;
